/**
 * Single source of truth for "may this viewer write to a task on this project?".
 *
 * The server is the authority — these predicates exist so the dashboard stops
 * lying to the user about what a click will do, not to enforce anything. They
 * mirror `ToDoService` (Focus-Bear/backend#2413, #2454):
 *
 * - any accepted project member may edit, move, assign and unassign a task;
 * - a task synced to an external board (Trello) mirrors out through its
 *   creator's credentials, so a colleague may only change its assignee;
 * - the creator, the project owner and project ADMINs may delete a task —
 *   except a synced one, which only its creator may delete.
 */
import { isAxiosError } from 'axios';
import { HTTP_STATS_CODE } from 'constants/general';
import { ToDoTask } from 'interfaces/common.interface';
import { Project, ProjectMemberRole } from 'interfaces/project.interface';
import { getViewerProjectRole } from 'utils/projects/project.util';

export interface ProjectTaskPermissions {
  canAddTask: boolean;
  isProjectMember: boolean;
  isProjectAdmin: boolean;
}

export interface TaskWritePermissions {
  canEditTask: boolean;
  canMoveTask: boolean;
  canAssignTask: boolean;
  canDeleteTask: boolean;
  /** Only a task's creator may name someone whose invite is still pending. */
  canAssignPendingMembers: boolean;
}

type ProjectMembership = Pick<Project, 'members' | 'owner_id'>;
type TaskOwnership = Pick<ToDoTask, 'user_id' | 'synced_project_id'>;

const isProjectAdminRole = (role?: ProjectMemberRole): boolean =>
  role === ProjectMemberRole.OWNER || role === ProjectMemberRole.ADMIN;

const isProjectMemberRole = (role?: ProjectMemberRole): boolean =>
  isProjectAdminRole(role) || role === ProjectMemberRole.MEMBER;

export const getProjectTaskPermissions = ({
  project,
  viewerId
}: {
  project: ProjectMembership;
  viewerId?: string;
}): ProjectTaskPermissions => {
  const viewerRole = getViewerProjectRole(project, viewerId);
  const isProjectMember = isProjectMemberRole(viewerRole);
  return {
    canAddTask: isProjectMember,
    isProjectMember,
    isProjectAdmin: isProjectAdminRole(viewerRole)
  };
};

/**
 * `task` is null while the drawer creates a new task — the viewer will own it.
 * An unknown creator is not assumed to be the viewer: offering a delete the
 * server then refuses is the dishonesty this module exists to prevent.
 */
const isTaskOwner = (task: TaskOwnership | null, viewerId?: string): boolean =>
  task === null || (Boolean(viewerId) && task.user_id === viewerId);

export const getTaskWritePermissions = ({
  task,
  projectPermissions,
  viewerId
}: {
  task: TaskOwnership | null;
  projectPermissions: Pick<
    ProjectTaskPermissions,
    'isProjectMember' | 'isProjectAdmin'
  >;
  viewerId?: string;
}): TaskWritePermissions => {
  const isOwner = isTaskOwner(task, viewerId);
  const isSynced = Boolean(task?.synced_project_id);
  const canWrite = isOwner || (projectPermissions.isProjectMember && !isSynced);
  return {
    canEditTask: canWrite,
    canMoveTask: canWrite,
    canAssignTask: isOwner || projectPermissions.isProjectMember,
    canDeleteTask: isOwner || (projectPermissions.isProjectAdmin && !isSynced),
    canAssignPendingMembers: isOwner
  };
};

/**
 * `validateUpdatingToDo` answers a refused write with a 401 (and a disallowed
 * reassignment or re-parent with a 403). The controls above should keep users
 * away from both, so if one still lands, say why instead of "could not update".
 */
const TASK_WRITE_REFUSAL_STATUSES: readonly number[] = [
  HTTP_STATS_CODE.UNAUTHORIZED,
  HTTP_STATS_CODE.FORBIDDEN
];

export const isTaskWriteRefusal = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false;
  }
  const status = error.response?.status;
  return status != null && TASK_WRITE_REFUSAL_STATUSES.includes(status);
};
